import _ from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { Button, Table, Input, Col, Row, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import domainModel from 'domain-model';
import { DatePicker } from '../DatePicker';
import keyedDebounceSingleton from '../../../utils/keyed-debounce-singleton';
import { calculateDaysEarlierWeekday, weekdays, weekdayMap } from './common';

type DeliveryRulesEditorType =
  typeof domainModel.deliveryRules.DeliveryRulesEditor;
type DeliveryRuleType = typeof domainModel.deliveryRules.DeliveryRule;

const { DeliveryRule, getWeekdayName, DeliveryRulesGeneralRegion } =
  domainModel.deliveryRules;

interface RegionHeaderProps {
  initialRegion: string;
  onChange: (oldRegion: string, newRegion: string) => void;
}

const RegionHeader: React.FC<RegionHeaderProps> = ({
  initialRegion,
  onChange,
}) => {
  const [localRegion, setLocalRegion] = useState(initialRegion);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(initialRegion, newValue);
    setLocalRegion(newValue);
  };

  if (localRegion === DeliveryRulesGeneralRegion) {
    return <>Generella leveransregler</>;
  }

  return (
    <Row>
      <Col>
        Regionspecifika leveransregler &nbsp;
        <Input
          type="text"
          value={localRegion}
          onChange={handleChange}
          onClick={(e: any) => e.stopPropagation()}
          onSelect={(e: any) => e.stopPropagation()}
        />
      </Col>
    </Row>
  );
};

function ExtendedRuleRowInput({
  weekday,
  region,
  date,
  deliveryRule,
  pickAndPackRule,
  onEnableChange,
  onTimeChange,
  onDaysBeforeChange,
  onBlockedDeliveryChange,
  onBlockedPickAndPackChange,
}: {
  weekday?: string;
  region?: string;
  date?: string;
  deliveryRule?: DeliveryRuleType;
  pickAndPackRule?: DeliveryRuleType;
  onEnableChange: (key: string, newEnabled: boolean) => void;
  onTimeChange: (key: string, newTime: string) => void;
  onDaysBeforeChange: (key: string, newDaysBefore: number) => void;
  onBlockedDeliveryChange?: (key: string, newBlocked: boolean) => void;
  onBlockedPickAndPackChange?: (key: string, newBlocked: boolean) => void;
}) {
  const key: string = (weekday || date)!;
  const daysBefore = deliveryRule?.getDaysBefore() ?? 0;
  const daysEarlierWeekday = calculateDaysEarlierWeekday(
    weekday,
    daysBefore,
    date,
  );
  const daysEarlierText = getWeekdayName(daysEarlierWeekday);

  const isBlocked = deliveryRule?.isBlocked() || pickAndPackRule;
  const isEnabled = !!deliveryRule || !!pickAndPackRule;

  return (
    <tr style={{ verticalAlign: 'middle' }} key={`${region} + ${key} `}>
      {weekday && (
        <td>
          <Label style={{ display: 'flex', alignItems: 'center' }}>
            <Input
              type="checkbox"
              checked={isEnabled}
              onChange={() => onEnableChange(key, !isEnabled)}
            />
            &nbsp;
            {weekdayMap[weekday]?.value}
          </Label>
        </td>
      )}
      {!weekday && !!date && (
        <td>
          <Label style={{ display: 'flex', alignItems: 'center' }}>
            <Input
              type="checkbox"
              checked={isEnabled}
              onChange={() => onEnableChange(key, !isEnabled)}
            />
            <DatePicker
              name="date"
              selected={moment(date).toDate()}
              isValid
              onChange={(inputName: string, newDate: Date) => {
                if (deliveryRule) {
                  deliveryRule.setDate(moment(newDate).format('YYYY-MM-DD'));
                }
                if (pickAndPackRule) {
                  pickAndPackRule.setDate(moment(newDate).format('YYYY-MM-DD'));
                }
                onEnableChange(key, true);
              }}
            ></DatePicker>
          </Label>
        </td>
      )}
      {onBlockedDeliveryChange && (
        <td>
          <Input
            type="checkbox"
            checked={deliveryRule?.isBlocked()}
            onChange={(e: any) =>
              onBlockedDeliveryChange(key, e.target.checked)
            }
          />
        </td>
      )}
      {onBlockedPickAndPackChange && (
        <td>
          <Input
            type="checkbox"
            checked={pickAndPackRule?.isBlocked()}
            onChange={(e: any) =>
              onBlockedPickAndPackChange(key, e.target.checked)
            }
          />
        </td>
      )}
      {deliveryRule && (
        <td>
          <Input
            type="text"
            disabled={isBlocked}
            value={deliveryRule?.getTime()}
            onChange={(e: any) => onTimeChange(key, e.target.value)}
          />
        </td>
      )}
      {deliveryRule && (
        <td>
          <Input
            type="number"
            disabled={isBlocked}
            value={deliveryRule.getDaysBefore()}
            onChange={(e: any) => onDaysBeforeChange(key, +e.target.value)}
          />
        </td>
      )}
      {deliveryRule && (
        <td>
          <span>
            {deliveryRule.getDaysBefore() > 0
              ? ` = ${daysEarlierText}`
              : ' = Samma dag'}
          </span>
        </td>
      )}
    </tr>
  );
}

interface ExtendedRulesInputProps {
  region: string;
  onChange: () => void;
  onRegionChanged: (newRegion: string) => void;
  deliveryRulesEditor?: DeliveryRulesEditorType;
  isOpen: boolean;
  onToggle: () => void;
  onlyExplicitDates?: boolean;
}

const ExtendedRulesInput: React.FC<ExtendedRulesInputProps> = props => {
  const {
    onChange,
    onRegionChanged,
    deliveryRulesEditor,
    region,
    isOpen,
    onToggle,
    onlyExplicitDates,
  } = props;

  const onEnableChangeWeekday = (key: string, newEnabled: boolean) => {
    if (newEnabled) {
      deliveryRulesEditor.getOrCreateDeliveryRuleWithRegionAndWeekday(
        region,
        key,
      );
    } else {
      deliveryRulesEditor.removeDeliveryRule(region, key);
      deliveryRulesEditor.removePickAndPackRule(key);
    }
    onChange();
  };

  const onEnableChangeDate = (key: string, newEnabled: boolean) => {
    if (newEnabled) {
      deliveryRulesEditor.getOrCreateDeliveryRuleWithRegionAndDate(region, key);
    } else {
      deliveryRulesEditor.removeDeliveryRule(region, key);
      deliveryRulesEditor.removePickAndPackRule(key);
    }
    onChange();
  };

  const onTimeChangeWeekday = (key: string, newTime: string) => {
    const deliveryRule: DeliveryRuleType =
      deliveryRulesEditor.getOrCreateDeliveryRuleWithRegionAndWeekday(
        region,
        key,
      );
    deliveryRule.setTime(newTime);
    onChange();
  };

  const onTimeChangeDate = (key: string, newTime: string) => {
    const deliveryRule: DeliveryRuleType =
      deliveryRulesEditor.getOrCreateDeliveryRuleWithRegionAndDate(region, key);
    deliveryRule.setTime(newTime);
    onChange();
  };

  const onDaysBeforeChangeWeekday = (key: string, newDaysBefore: number) => {
    const deliveryRule: DeliveryRuleType =
      deliveryRulesEditor.getOrCreateDeliveryRuleWithRegionAndWeekday(
        region,
        key,
      );
    deliveryRule.setDaysBefore(newDaysBefore);
    onChange();
  };
  const onDaysBeforeChangeDate = (key: string, newDaysBefore: number) => {
    const deliveryRule: DeliveryRuleType =
      deliveryRulesEditor.getOrCreateDeliveryRuleWithRegionAndDate(region, key);
    deliveryRule.setDaysBefore(newDaysBefore);
    onChange();
  };

  const onBlockedDeliveryChange = (key: string, deliveryIsBlocked: boolean) => {
    if (deliveryIsBlocked) {
      const deliveryRule: DeliveryRuleType =
        deliveryRulesEditor.getOrCreateDeliveryRuleWithRegionAndDate(
          region,
          key,
        );
      deliveryRule.setBlockedDelivery(deliveryIsBlocked);
    }
    if (!deliveryIsBlocked) {
      const deliveryRule: DeliveryRuleType =
        deliveryRulesEditor.getRuleWithRegionAndKey(region, key);
      if (deliveryRule) {
        if (deliveryRule.getTime()) {
          deliveryRule.setBlockedDelivery(deliveryIsBlocked);
        } else {
          deliveryRulesEditor.removeDeliveryRule(
            deliveryRule.getRegion(),
            deliveryRule.getKey(),
          );
        }
      }
    }
    onChange();
  };

  const onBlockedPickAndPackChange = (
    key: string,
    pickAndPackBlocked: boolean,
  ) => {
    deliveryRulesEditor.createOrRemovePickAndPackRule(key, pickAndPackBlocked);
    if (pickAndPackBlocked) {
      const deliveryRule: DeliveryRuleType =
        deliveryRulesEditor.getRuleWithRegionAndKey(region, key);
      if (!deliveryRule.isBlocked()) {
        deliveryRulesEditor.removeDeliveryRule(
          deliveryRule.getRegion(),
          deliveryRule.getKey(),
        );
      }
    }

    onChange();
  };

  const onRegionChange = (oldRegion: string, newRegion: string) => {
    keyedDebounceSingleton.debounce({
      queue: 'delivery_rules',
      key: 'region_change',
      delayedFunction: () => {
        deliveryRulesEditor.changeRegion(oldRegion, newRegion);
        onRegionChanged(newRegion);
        onChange();
      },
      delay: 1000,
      retry: true,
    });
  };

  const rulesForRegionForDate = deliveryRulesEditor
    .getRulesWithRegion(region)
    .filter((deliveryRule: DeliveryRuleType) => deliveryRule.getDate());
  const blockedPickAndPackRules =
    deliveryRulesEditor.getPickAndPackBlockedRules();

  const datesForAllRules = _(rulesForRegionForDate)
    .concat(blockedPickAndPackRules)
    .map((x: DeliveryRuleType) => x.getDate())
    .uniq()
    .value();

  return (
    <div>
      {!onlyExplicitDates && (
        <h4 onClick={onToggle} style={{ cursor: 'pointer' }}>
          <Row>
            <Col sm="auto">
              {isOpen ? (
                <FontAwesomeIcon icon={faCaretDown} />
              ) : (
                <FontAwesomeIcon icon={faCaretRight} />
              )}
            </Col>
            <Col sm="auto">
              <RegionHeader
                key="regionInput"
                initialRegion={region}
                onChange={onRegionChange}
              />
            </Col>
          </Row>
        </h4>
      )}
      {isOpen && !onlyExplicitDates && (
        <Table striped>
          <thead>
            <tr>
              <th>Leveransdag</th>
              <th>Bryttid</th>
              <th>Antal dagar tidigare</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {weekdays.map(weekday => {
              const deliveryRule = deliveryRulesEditor.getRuleWithRegionAndKey(
                region,
                weekday.key,
              );

              return (
                <ExtendedRuleRowInput
                  key={weekday.key}
                  weekday={weekday.key}
                  region={region}
                  deliveryRule={deliveryRule}
                  onEnableChange={onEnableChangeWeekday}
                  onTimeChange={onTimeChangeWeekday}
                  onDaysBeforeChange={onDaysBeforeChangeWeekday}
                />
              );
            })}
          </tbody>
        </Table>
      )}
      {isOpen && onlyExplicitDates && (
        <>
          <h4>Avvikande datum</h4>
          {JSON.stringify(datesForAllRules)}

          {datesForAllRules && datesForAllRules.length > 0 && (
            <Table striped>
              <thead>
                <tr>
                  <th>Leveransdag</th>
                  <th>Ingen leverans</th>
                  <th>Lagret stängt</th>
                  <th>Bryttid</th>
                  <th># dagar tidigare</th>
                </tr>
              </thead>
              <tbody>
                {datesForAllRules.map(date => (
                  <ExtendedRuleRowInput
                    key={date}
                    date={date}
                    region={region}
                    deliveryRule={deliveryRulesEditor.getRuleWithRegionAndKey(
                      region,
                      date,
                    )}
                    pickAndPackRule={deliveryRulesEditor.getBlockedPickAndPackRuleWithDate(
                      date,
                    )}
                    onEnableChange={onEnableChangeDate}
                    onTimeChange={onTimeChangeDate}
                    onDaysBeforeChange={onDaysBeforeChangeDate}
                    onBlockedDeliveryChange={onBlockedDeliveryChange}
                    onBlockedPickAndPackChange={onBlockedPickAndPackChange}
                  />
                ))}
              </tbody>
            </Table>
          )}
          <Button
            onClick={() => {
              const newRule = new DeliveryRule();
              newRule.setDate(moment(Date.now()).format('YYYY-MM-DD'));
              newRule.setRegion(region);
              newRule.setTime('12:00');
              newRule.setDaysBefore(1);
              deliveryRulesEditor.addDeliveryRule(newRule);
              onChange();
            }}
          >
            Lägg till datum
          </Button>
          <hr />
        </>
      )}
    </div>
  );
};

export default ExtendedRulesInput;
